import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImagePlusIcon, SendIcon, XIcon } from 'lucide-react';
import { toast } from '@noor/lib';
import { Textarea } from '@noor/ui';
import { MERCHANT_CHAT_ACCEPT, validateMerchantChatFile } from '@/entities/merchant-chat';
import { useSendMerchantMessage } from '../model/use-send-merchant-message';
import { useStartMerchantChat } from '../model/use-start-merchant-chat';

/** The old composer's 200-character draft cap. */
const MESSAGE_MAX_LENGTH = 200;

type ComposerProps =
  | { mode: 'reply'; chatId: number; onCreated?: undefined }
  | { mode: 'create'; chatId?: undefined; onCreated: (id: number) => void };

/**
 * The message composer — the old `ChatMessageInput`, ported. Text plus at
 * most one image (the field locks while a file is staged, as the old one
 * did); Enter sends, Shift+Enter breaks. In `create` mode the first send
 * opens the thread and the drawer navigates into it.
 */
export function MerchantChatComposer(props: ComposerProps) {
  const { t } = useTranslation('common');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  /** Staged-file replace target (tapping a chip swaps it instead of adding). */
  const [replacing, setReplacing] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textRef = useRef<HTMLTextAreaElement | null>(null);

  const send = useSendMerchantMessage();
  const start = useStartMerchantChat();
  const isPending = send.isPending || start.isPending;

  useEffect(() => {
    textRef.current?.focus();
  }, []);

  const reset = () => {
    setMessage('');
    setFiles([]);
    setReplacing(null);
    textRef.current?.focus();
  };

  const handleSend = () => {
    if (!message && files.length === 0) {
      textRef.current?.focus();
      return;
    }
    const hasText = message
      .split('\n')
      .filter((line) => line.trim() !== '')
      .join('\n').length;
    if (!hasText && files.length === 0) return;

    if (props.mode === 'reply') {
      send.mutate({ chatId: props.chatId, message, files }, { onSuccess: () => reset() });
    } else {
      start.mutate(
        { message, files },
        {
          onSuccess: (id) => {
            reset();
            props.onCreated(id);
          },
        },
      );
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0];
    // Reset so picking the same file twice still fires `onChange`.
    event.target.value = '';
    if (!picked) {
      setReplacing(null);
      return;
    }
    // A replace swaps its target; anything else counts toward the cap.
    const others =
      replacing === null ? files : files.filter((file) => file.name !== replacing);
    const error = validateMerchantChatFile(others, picked);
    if (error) {
      toast.error(t(error));
      setReplacing(null);
      return;
    }
    setFiles(
      replacing === null
        ? [...files, picked]
        : files.map((file) => (file.name === replacing ? picked : file)),
    );
    setReplacing(null);
  };

  return (
    <div className="border-t border-border bg-card p-3">
      {files.length > 0 && (
        <div className="mb-2 flex flex-row gap-2">
          {files.map((file) => (
            <span
              key={file.name}
              className="inline-flex max-w-[155px] items-center gap-1 overflow-hidden rounded-full bg-muted py-1 pr-1 pl-3 text-sm whitespace-nowrap text-foreground"
              title={file.name}
            >
              <button
                type="button"
                onClick={() => {
                  setReplacing(file.name);
                  fileInputRef.current?.click();
                }}
                className="cursor-pointer overflow-hidden text-ellipsis outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring/40"
              >
                {file.name}
              </button>
              <button
                type="button"
                aria-label={t('chat.remove_file')}
                onClick={() => {
                  setFiles((prev) => prev.filter((item) => item.name !== file.name));
                }}
                className="hover:bg-secondary-hover shrink-0 cursor-pointer rounded-full p-0.5 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
              >
                <XIcon className="size-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex w-full items-center gap-2">
        <Textarea
          ref={textRef}
          value={message}
          disabled={files.length > 0}
          onChange={(event) => {
            if (event.target.value.length <= MESSAGE_MAX_LENGTH) {
              setMessage(event.target.value);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              handleSend();
            }
          }}
          placeholder={t('chat.message_input_placeholder')}
          aria-label={t('chat.message_input_placeholder')}
          rows={1}
          className="max-h-[120px] min-h-14"
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={isPending}
          aria-label={t('chat.send')}
          className="shrink-0 cursor-pointer rounded-full p-2 text-primary transition-opacity outline-none hover:opacity-80 focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:cursor-default"
        >
          <SendIcon className="size-5" />
        </button>
        <button
          type="button"
          onClick={() => {
            setReplacing(null);
            fileInputRef.current?.click();
          }}
          aria-label={t('chat.attach')}
          className="hover:bg-secondary-hover flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-lg bg-muted text-foreground transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
        >
          <ImagePlusIcon className="size-5" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={MERCHANT_CHAT_ACCEPT}
          onChange={handleFileChange}
          className="absolute h-0 w-0 overflow-hidden"
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>
    </div>
  );
}
